import React, {useEffect, useRef, useState} from 'react';
import {withRouter} from 'react-router-dom';
import {Button, Dialog} from '@betareader/design-system';

import {withTranslation} from 'react-i18next';

// pop-ups
import {toast} from 'react-toastify';

// redux
import {push} from '../../../navigation';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';

import {cancelSubscription} from '../../../modules/subscription';

import {getUserProfile} from '../../../modules/user';

import {authUtil, uxAnalyticsUtil} from '../../../utils';
import usePrevious from '../../../hooks/usePrevious';

const CANCEL_STAGE = {
  INITIATED: 0,
  ANSWERING_SURVEY: 1,
  CANCELLING: 2,
  DONE: 3
};

const cancelReasons = [
  'technical_issues',
  'too_expensive',
  'finished_my_beta',
  'switching_to_another_product',
  'missing_features',
  'did_not_meet_expectations',
  'other'
];

export const CancelSubscriptionModal = ({
  userProfile,
  cancelSubscription,
  getUserProfile,
  onClose,
  open,
  t
}) => {
  const refreshedUserProfileCount = useRef(0);
  const [currentStage, setCurrentStage] = useState(CANCEL_STAGE.INITIATED);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  const previousUserProfile = usePrevious(userProfile);
  const refreshUserProfileTimer = useRef();

  // runs on first render
  useEffect(() => {
    setCurrentStage(CANCEL_STAGE.INITIATED);
    refreshedUserProfileCount.current = 0;
    return () => clearInterval(refreshUserProfileTimer.current);
  }, []);

  useEffect(() => {
    if (previousUserProfile) {
      const previousPricingPlanId =
        previousUserProfile.account?.subscription?.pricingPlan?.externalId;
      const currentPricingPlanId =
        userProfile.account.subscription.pricingPlan.externalId;
      if (previousPricingPlanId !== currentPricingPlanId) {
        // if we had a previousUserProfile and the pricing plan id has changed, we have moved plan and can close the modal
        close();
      }
    }
  }, [userProfile?.account?.subscription?.pricingPlan?.externalId]);

  const handleCancelSubscription = async () => {
    const idToken = await authUtil.getFreshIdToken();
    setCurrentStage(CANCEL_STAGE.CANCELLING);
    cancelSubscription(
      {
        idToken,
        subscriptionId: userProfile.account.subscription._id,
        cancelReason,
        cancelFeedback
      },
      response => {
        if (!response.ok) {
          toast.error(t('SomethingWentWrong'));
          uxAnalyticsUtil.trackException({
            description: response,
            fatal: true
          });
          close();
        } else {
          // track cancellation to GA
          uxAnalyticsUtil.trackEvent({
            category: 'Subscriptions',
            action: 'cancelled-subscription',
            label: userProfile.account.product.internalId
          });
          onSuccessfulCancel();
        }
      }
    );
  };

  const refreshUserProfile = async () => {
    if (refreshedUserProfileCount.current < 3) {
      refreshedUserProfileCount.current += 1;
      const idToken = await authUtil.getFreshIdToken();
      getUserProfile(idToken);
    } else {
      clearInterval(refreshUserProfileTimer.current);
      close();
    }
  };

  const onSuccessfulCancel = () => {
    // repeat with the interval of 1.5 seconds
    refreshUserProfileTimer.current = setInterval(refreshUserProfile, 1500);
  };

  const close = async () => {
    const idToken = await authUtil.getFreshIdToken();
    getUserProfile(idToken);
    clearInterval(refreshUserProfileTimer.current);
    setCurrentStage(CANCEL_STAGE.INITIATED);
    if (onClose) {
      onClose();
    }
  };

  const handleConfirmClick = () => {
    switch (currentStage) {
      case CANCEL_STAGE.INITIATED:
        setCurrentStage(CANCEL_STAGE.ANSWERING_SURVEY);
        break;
      case CANCEL_STAGE.ANSWERING_SURVEY:
        handleCancelSubscription();
        break;
      default:
        break;
    }
  };

  const handleCancelReasonChange = event => {
    setCancelReason(event.target.value);
  };

  const handleCancelFeedbackChange = event => {
    setCancelFeedback(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t('CancelSubscription')}
      actions={<>
        <Button
          variant="secondary"
          disabled={currentStage === CANCEL_STAGE.CANCELLING}
          onClick={currentStage === CANCEL_STAGE.ANSWERING_SURVEY
            ? () => setCurrentStage(CANCEL_STAGE.INITIATED) : close}
        >
          {currentStage === CANCEL_STAGE.INITIATED && t('No')}
          {currentStage === CANCEL_STAGE.ANSWERING_SURVEY && t('Back')}
        </Button>
        <Button
          variant="danger"
          disabled={currentStage === CANCEL_STAGE.CANCELLING}
          onClick={handleConfirmClick}
        >
          {currentStage === CANCEL_STAGE.INITIATED && t('Yes')}
          {currentStage === CANCEL_STAGE.ANSWERING_SURVEY && t('CancelSubscription')}
        </Button>
      </>}
    >
        {currentStage === CANCEL_STAGE.INITIATED && (
          <p>{t('CancelSubscriptionConfirmation')}</p>
        )}
        {currentStage === CANCEL_STAGE.CANCELLING && (
          <div role="status">{t('WaitingForConfirmation')}</div>
        )}
        {currentStage === CANCEL_STAGE.ANSWERING_SURVEY && (
          <div>
            <fieldset>
              <legend>{t('CancelReason')}</legend>
            {cancelReasons.map(reason => (
              <label key={reason} style={{display: 'block', margin: '0.5em 0'}}>
                <input
                  type="radio"
                  name='CancelReason'
                  value={reason}
                  checked={cancelReason === reason}
                  onChange={handleCancelReasonChange}
                />{' '}{t(`cancel_reason_${reason}`)}
              </label>
            ))}
            </fieldset>
            <label>
              {t('TellUsMore')}
              <textarea
                placeholder={t('TellUsMore')}
                value={cancelFeedback}
                onChange={handleCancelFeedbackChange}
                style={{display: 'block', width: '100%', minHeight: 90}}
              />
            </label>
          </div>
        )}
        {currentStage === CANCEL_STAGE.DONE && (
          <p>{t('YourSubscriptionHasBeenCancelled')}</p>
        )}
    </Dialog>
  );
};

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  idToken: state.user.idToken,
  userProfile: state.user.userProfile
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      cancelSubscription,
      getUserProfile,
      changePage: newPage => push(newPage)
    },
    dispatch
  );

export default withTranslation()(
  withRouter(
    connect(mapStateToProps, mapDispatchToProps)(CancelSubscriptionModal)
  )
);
